'use client'

import * as React from 'react'
import Image from 'next/image'

import { useCookies } from 'react-cookie'
import { ModalWithError } from '@web/components/prod/modal-with-error'
import { LFS_URL } from '@web/utils/env'
import { Button } from '@repo/design-system-cf/components/button'
import { Typography } from '@repo/design-system-cf/components/typography'
import { useTranslations } from 'next-intl'
import { IconFacebookColourFull } from '@repo/design-system-cf/components/icons'
import { GetAFreeBundleModalSuccess } from './get-a-free-bundle-modal-success'

interface GetAFreeBundleModalProps {
  isOpen: boolean
  onClose: () => void
  error?: string | null
  productId: number
  shareUrl: string
}

export const HAS_SHARED_DG_COOKIE_NAME = 'hasShared-daily-gifts'
export const HAS_SEEN_COOKIE_NAME = 'daily-gifts-modal-seen'
export const DAILY_GIFTS_MODAL_ID = 'get-a-free-bundle-modal'

export function GetAFreeBundleModal({
  isOpen,
  onClose,
  error = null,
  productId,
  shareUrl,
}: GetAFreeBundleModalProps) {
  const t = useTranslations('pages.daily-gifts.banner_modal')
  const [cookies, setCookie] = useCookies([HAS_SHARED_DG_COOKIE_NAME, HAS_SEEN_COOKIE_NAME])
  const [hasShared, setHasShared] = React.useState(cookies[HAS_SHARED_DG_COOKIE_NAME] === true)

  const onShare = () => {
    setCookie(HAS_SHARED_DG_COOKIE_NAME, 'true', {
      path: '/',
      expires: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000), // 30 days
    })
    setHasShared(true)
  }

  if (hasShared) {
    return (
      <div id={DAILY_GIFTS_MODAL_ID}>
        <GetAFreeBundleModalSuccess
          isOpen={isOpen}
          onClose={onClose}
          error={error}
          productId={productId}
        />
      </div>
    )
  }

  return (
    <div id={DAILY_GIFTS_MODAL_ID}>
      <ModalWithError
        isOpen={isOpen}
        onClose={onClose}
        error={error}
        className="max-w-[720px] max-h-[90dvh]"
      >
        <Typography variant="cf-title-05" className="text-center mb-6">
          {t('title_modal')}
        </Typography>
        <div className="grid grid-cols-1 md:grid-cols-[330px_1fr] gap-2 md:gap-6">
          <div className="md:max-w-[330px] aspect-auto object-contain relative items-start">
            <Image
              src={`${LFS_URL}/web/pages/daily-gifts/share-volume-17.jpg`}
              alt="Daily Gifts Surprise Bundle Volume 17"
              className=" aspect-auto object-contain"
              width={0}
              height={0}
              sizes="100vw"
              style={{ width: '100%', height: 'auto' }}
            />
          </div>

          {/* Right content */}
          <div className="flex flex-col py-4 pr-4">
            <div className="flex flex-col gap-3 md:max-w-[280px]">
              <Typography variant="cf-title-03">{t('title_modal_2')}</Typography>

              <Typography variant="body-02" className=" ">
                {t.rich('description_modal', { b: (chunks: React.ReactNode) => <b>{chunks}</b> })}
              </Typography>
            </div>

            <div className="mt-5">
              <a href={shareUrl} target="_blank" rel="noopener noreferrer">
                <Button
                  onClick={onShare}
                  iconLeft={<IconFacebookColourFull />}
                  className="bg-[#0866FF] border border-[#0866FF] text-cf-white hover:bg-[#1669d7]"
                >
                  {t('share')}
                </Button>
              </a>
            </div>
          </div>
        </div>
      </ModalWithError>
    </div>
  )
}
